/**
 * <cart-panel-items>
 *
 * Extends CartItems (cart.js) so qty / remove / debounced fetch / loading
 * overlays / errors are not reimplemented. We override:
 *   - getSectionsToRender() to point at cart-panel
 *   - onCartUpdate() to swap the cart-panel items + footer regions
 *   - enableLoading()/disableLoading() because the base class hardcodes
 *     #main-cart-items / #CartDrawer-CartItems IDs (CartPanel uses its own).
 */
class CartPanelItems extends CartItems {
  constructor() {
    super();

    // Pressing Enter inside the qty input would otherwise submit the panel's
    // <form action="/cart" method="post">. The form's first submit-type button
    // is `<button name="checkout">` (cart-panel-footer), so Enter routes the
    // user straight to checkout — surprising and destructive. Intercept it,
    // commit the typed value via a synthetic change event (the base CartItems
    // debounced handler picks this up and runs updateQuantity), and stay in
    // the panel. We dispatch change explicitly because relying on blur is
    // unreliable: blur only fires `change` if the browser tracked an in-focus
    // user edit, which doesn't happen for programmatic / paste-style edits.
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      const target = event.target;
      if (!target || target.tagName !== 'INPUT' || target.type !== 'number') return;
      event.preventDefault();
      target.dispatchEvent(new Event('change', { bubbles: true }));
      target.blur();
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartPanel',
        section: 'cart-panel',
        selector: '#CartPanel-CartItems',
      },
      {
        id: 'CartPanel-Footer',
        section: 'cart-panel',
        selector: '[data-region="cart-panel-footer"]',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'CartPanel-LiveRegionText',
        section: 'cart-panel',
        selector: '#CartPanel-LiveRegionText',
      },
    ];
  }

  enableLoading(line) {
    const wrap = document.getElementById('CartPanel-CartItems');
    wrap?.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartPanel-Item-${line} .loading-overlay`);
    cartItemElements.forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }
  }

  disableLoading(line) {
    const wrap = document.getElementById('CartPanel-CartItems');
    wrap?.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartPanel-Item-${line} .loading-overlay`);
    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  /**
   * Override the base CartItems subscriber to also ignore `source: 'cart-panel'`
   * events. The panel's own mutations (rec add, share-cart restore, etc.)
   * already render the response sections in-place via renderContents — letting
   * the inherited subscriber re-trigger onCartUpdate would fire a redundant
   * /cart?section_id=cart-panel fetch and double the perceived latency.
   */
  connectedCallback() {
    if (this.cartUpdateUnsubscriber) return;
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && (event.source === 'cart-items' || event.source === 'cart-panel')) return;
      this.onCartUpdate();
    });
  }

  /**
   * When the cart is updated outside this panel (PDP form, etc.), re-fetch
   * the cart-panel section and replace items + footer in place. Mirrors
   * CartDrawerItems.onCartUpdate but targets cart-panel selectors.
   */
  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-panel`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = [
          '#CartPanel-CartItems',
          '[data-region="cart-panel-footer"]',
        ];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }
      })
      .catch((e) => console.error('cart-panel-items: refresh failed', e));
  }

  /**
   * Override the base CartItems.updateQuantity for two reasons:
   *
   *  1. The base swaps section HTML into element IDs that don't exist in the
   *     panel's empty-state branch (`cart-panel__main--empty` vs the form
   *     subtree), so a decrement that empties the cart would throw.
   *  2. We want to render from the /cart/change.js response sections directly
   *     (the same pattern product-form.js / _handleRecAdd use) instead of
   *     paying for a second + third fetch via panel.refreshSections().
   *
   * panel.renderContents(parsedState) handles the empty <-> non-empty
   * transition cleanly, so we hand the parsed response to it and skip the
   * follow-up section fetches entirely.
   */
  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    // Wipe any stale per-line / global error from a previous action so the
    // user doesn't see "you can have N" lingering after they've already
    // adjusted the quantity. The success path replaces this DOM via
    // renderContents, but starting clean covers paths that don't replace
    // (422, network failure) too.
    this.updateLiveRegions(line, '');
    const cartErrors = document.getElementById('CartPanel-CartErrors');
    if (cartErrors) cartErrors.textContent = '';

    const requestedQty = parseInt(quantity, 10);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          // Quantity-limit / sold-out / etc. Revert the input, surface the
          // message via the line's `.cart-item__error-text`, and publish
          // cartError so other subscribers can react. Leaves the rest of the
          // UI untouched — the server didn't change the cart.
          const quantityElement = document.getElementById(`Panel-quantity-${line}`);
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cart-panel',
            variantId,
            errors: parsedState.errors,
            message: parsedState.message,
          });
          return;
        }

        const panel = this.closest('cart-panel') || document.querySelector('cart-panel');
        if (panel && typeof panel.renderContents === 'function') {
          panel.renderContents(parsedState);
        }

        // Soft-clamp surfacing: Shopify sometimes accepts (200 OK) while
        // capping quantity below what the user requested — typically when
        // the buyer was already at the line's cap before this request. The
        // base CartItems code shows `cartStrings.quantityError` here so the
        // user understands why the displayed qty doesn't match the click.
        // Without this branch the input quietly snaps to the clamped value
        // and the user has no idea why (this was the "warning at 4 → 6
        // succeeds" report — a stale clamp message from this exact
        // pathway, set on a previous action and never re-surfaced).
        if (requestedQty !== 0) {
          // Match by variant_id rather than positional `items[line - 1]`.
          // /cart/change.js with `line` reorders the cart so the changed
          // line moves to the front (Shopify behavior — this is documented
          // and observable: "the changed item is moved to the first
          // position"). Positional indexing after a successful change
          // would surface a clamp warning for the WRONG line.
          const targetVariantId = parseInt(variantId, 10);
          const updatedItem = Number.isFinite(targetVariantId)
            ? parsedState.items.find((it) => it.variant_id === targetVariantId)
            : parsedState.items[line - 1];
          const actualQty = updatedItem ? updatedItem.quantity : null;
          if (actualQty !== null && actualQty !== requestedQty && window.cartStrings && window.cartStrings.quantityError) {
            this.updateLiveRegions(
              line,
              window.cartStrings.quantityError.replace('[quantity]', actualQty)
            );
          }
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-panel',
          cartData: parsedState,
          variantId,
        });
      })
      .catch((e) => {
        // Network failure, non-JSON body, etc. Show the generic error in the
        // panel's error region (matches base CartItems behavior) and publish
        // cartError. The .finally below clears the loading overlay so the
        // user can retry — without it the row would stay stuck spinning.
        console.error('cart-panel-items: updateQuantity failed', e);
        const errors = document.getElementById('CartPanel-CartErrors');
        if (errors && window.cartStrings && window.cartStrings.error) {
          errors.textContent = window.cartStrings.error;
        }
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'cart-panel',
          variantId,
          message: (e && e.message) || 'Network error',
        });
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }
}

customElements.define('cart-panel-items', CartPanelItems);
